import { useQuery } from "@tanstack/react-query";
import { getBackendUrl } from "api/common";
import { axiosClient } from "api/http/config";
import { withErrorBoundary } from "app/ErrorBoundary/ErrorBoundaryDashboard";
import TabsCenter from "features/CenterContent/shared/Tabs/TabsCenter";
import { useMemo } from "react";
import {
  BusModule,
  PanelHeaderDivider,
  PanelModuleGrid,
  PanelScrollArea,
  PanelScrollContent,
} from "features/CenterContent/VehicleDetails/AlternativeTransportDetails/altTransportPanelLayout";
import { BackupVehicleInfo } from "features/CenterContent/VehicleDetails/BackupAlternativeTransportDetails/BackupVehicleInfo";
import OperationalLog from "features/CenterContent/VehicleDetails/shared/OperationalLog";
import { useParams } from "react-router-dom";
import { ExpandableInfoMessage } from "shared/components/feedback/InfoMessage/ExpandableInfoMessage/ExpandableInfoMessage";
import { Module } from "shared/components/Module";
import { Text, VStack } from "shared/components/Spor";
import { reserveAlternativeTransportSchema } from "shared/types/alternativeTransport";
import * as z from "zod";

const BackupAlternativeTransportDetails = () => {
  const { vehicleUuid } = useParams();

  const { status, data: backupVehicle } = useQuery({
    queryKey: ["backupDetails", "backup", vehicleUuid],
    refetchInterval: 30000,
    queryFn: async ({ signal }) =>
      axiosClient
        .get<
          z.infer<typeof reserveAlternativeTransportSchema>
        >(`${getBackendUrl()}/alternativeTransports/backup/${vehicleUuid}`, { signal })
        .then((res) => reserveAlternativeTransportSchema.parse(res.data)),
  });

  const sectionState = useMemo(
    () => ({
      fetchStatus: status,
      isEmpty: !backupVehicle,
      emptyReason: "Fant ingen detaljer om bussen",
    }),
    [backupVehicle, status],
  );

  return (
    <>
      <TabsCenter isBackUpBus />
      <BusModule sectionState={sectionState}>
        {backupVehicle && (
          <>
            <PanelHeaderDivider>
              <BackupVehicleInfo reserveAlternativeTransport={backupVehicle} />
            </PanelHeaderDivider>
            <PanelScrollContent>
              <PanelScrollArea>
                <PanelModuleGrid>
                  <VStack
                    style={{
                      width: "100%",
                      padding: 0,
                      alignItems: "stretch",
                    }}
                  >
                    {backupVehicle.notes.length > 0 && (
                      <div style={{ width: "100%", padding: "0.75rem" }}>
                        <ExpandableInfoMessage
                          semantics="info"
                          title="Info fra Brudd"
                        >
                          {backupVehicle.notes.map((note) => (
                            <Text key={note}>{note}</Text>
                          ))}
                        </ExpandableInfoMessage>
                      </div>
                    )}
                    <Module title="Logg">
                      <OperationalLog
                        vehicleId={backupVehicle.id}
                        logs={backupVehicle.logs ?? []}
                      />
                    </Module>
                  </VStack>
                </PanelModuleGrid>
              </PanelScrollArea>
            </PanelScrollContent>
          </>
        )}
      </BusModule>
    </>
  );
};

export default withErrorBoundary(BackupAlternativeTransportDetails);
